import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import { toast } from "sonner";
import { ArrowLeft, ArrowCounterClockwise, Eye } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AppHeader } from "@/components/AppHeader";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function EmailTemplates() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [key, setKey] = useState("install_reminder");
  const [draft, setDraft] = useState({ subject: "", headline: "", body: "" });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get("/email-templates").then(({ data: d }) => {
      setData(d);
      setDraft({
        subject: d.templates[key].subject,
        headline: d.templates[key].headline || "",
        body: d.templates[key].body,
      });
    });

  useEffect(() => {
    load().catch(() => toast.error("Could not load templates"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pick = (k) => {
    setKey(k);
    setPreview(null);
    setDraft({
      subject: data.templates[k].subject,
      headline: data.templates[k].headline || "",
      body: data.templates[k].body,
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/email-templates/${key}`, draft);
      toast.success("Template saved");
      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not save",
      );
    }
    setBusy(false);
  };

  const reset = () => setDraft(data.defaults[key]);

  const showPreview = async () => {
    const { data: p } = await api.post(
      `/email-templates/${key}/preview`,
      draft,
    );
    setPreview(p);
  };

  if (!data) return <div className="grain min-h-screen" />;

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Settings / Email" />
      <main className="relative z-10 mx-auto max-w-3xl px-4 pb-28 pt-6">
        <button
          onClick={() => navigate("/settings")}
          data-testid="back-button"
          className="mb-4 inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white"
        >
          <ArrowLeft size={16} weight="bold" /> Settings
        </button>
        <div className="text-xs uppercase tracking-widest text-zinc-600">
          Customer messaging
        </div>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
          Email Templates
        </h1>
        <p className="mt-1 text-sm text-zinc-500">
          Edit what goes out to customers — account emails, plus the opening
          message of quote and invoice emails
        </p>

        <div
          className="mt-6 flex flex-wrap gap-2"
          data-testid="email-template-actions"
        >
          <Button
            data-testid="save-template-button"
            disabled={busy}
            onClick={save}
            className="h-12 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
          >
            {busy ? "Saving…" : "Save template"}
          </Button>
          <Button
            data-testid="preview-template-button"
            variant="outline"
            onClick={showPreview}
            className="h-12 border-zinc-700 bg-transparent px-4 text-[11px] uppercase tracking-widest text-zinc-300 hover:bg-zinc-800"
          >
            <Eye size={15} weight="bold" className="mr-2" /> Preview
          </Button>
          <Button
            data-testid="reset-template-button"
            variant="outline"
            onClick={reset}
            className="h-12 border-zinc-700 bg-transparent px-4 text-[11px] uppercase tracking-widest text-zinc-300 hover:bg-zinc-800"
          >
            <ArrowCounterClockwise size={15} weight="bold" className="mr-2" />{" "}
            Reset to default
          </Button>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {Object.entries(data.templates).map(([k, t]) => (
            <button
              key={k}
              data-testid={`template-tab-${k}`}
              onClick={() => pick(k)}
              className={`h-12 rounded-md border px-4 text-xs font-semibold uppercase tracking-widest transition-colors duration-150 ${
                key === k
                  ? "border-indigo-500 bg-indigo-500 text-white"
                  : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
              }`}
            >
              {t.label}
              {t.customized && key !== k ? " ·" : ""}
            </button>
          ))}
        </div>

        <div className="mt-6 space-y-4 rounded-lg border border-zinc-800 bg-zinc-900 p-4">
          {data.templates[key].detail && (
            <p className="text-xs text-zinc-400" data-testid="template-detail">
              {data.templates[key].detail}
            </p>
          )}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Subject
            </Label>
            <Input
              data-testid="template-subject-input"
              value={draft.subject}
              onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
              className="h-12 border-zinc-800 bg-zinc-950"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Headline
            </Label>
            <Input
              data-testid="template-headline-input"
              value={draft.headline}
              onChange={(e) => setDraft({ ...draft, headline: e.target.value })}
              placeholder="The big line at the top of the email — leave blank for none"
              className="h-12 border-zinc-800 bg-zinc-950"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Message
            </Label>
            <Textarea
              data-testid="template-body-input"
              rows={10}
              value={draft.body}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
              className="border-zinc-800 bg-zinc-950"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {(data.templates[key].tags || data.placeholders).map((p) => (
              <button
                key={p}
                data-testid={`placeholder-${p}`}
                onClick={() =>
                  setDraft({ ...draft, body: `${draft.body}{${p}}` })
                }
                className="font-mono-data rounded-full border border-zinc-800 bg-zinc-950 px-2.5 py-1 text-[10px] text-indigo-300 hover:border-indigo-500"
              >
                {`{${p}}`}
              </button>
            ))}
          </div>
          <p className="text-xs text-zinc-500">
            Click a tag to insert it. Each blank line becomes a new paragraph in
            the email.
          </p>
        </div>

        {preview && (
          <div
            className="mt-4 rounded-lg border border-zinc-800 bg-white p-4 text-black"
            data-testid="template-preview"
          >
            <div className="mb-2 text-xs font-bold uppercase tracking-widest text-zinc-500">
              {preview.subject}
            </div>
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(preview.html),
              }}
            />
          </div>
        )}
      </main>
    </div>
  );
}
